import { cn } from '@/lib/utils';

export type DeltaDirection = 'up' | 'down' | 'flat';

interface IDeltaLabelProps {
  /** Direction of change. `null` renders a muted no-data dash. */
  direction: DeltaDirection | null;
  /** Text to the right of the arrow — e.g. "12%", "3d", "+6 WoW". */
  text: string;
  /** When true, `up` is green and `down` is red. When false (default), invert. */
  higherIsBetter?: boolean;
  className?: string;
}

const ARROW: Record<DeltaDirection, string> = {
  up: '▲',
  down: '▼',
  flat: '–',
};

/**
 * Shared delta badge used by Metric View's summary tiles and P85 cells.
 * Follows the same visual pattern as Management Dashboard's OverviewTab but
 * exposed as a generic component so both can share — and so future cards on
 * other pages can reuse the same green/red semantics without re-deriving them.
 */
export function DeltaLabel({
  direction,
  text,
  higherIsBetter = true,
  className,
}: Readonly<IDeltaLabelProps>) {
  if (direction === null) {
    return <span className={cn('text-xs text-muted-foreground', className)}>—</span>;
  }

  const colorClass = resolveColor(direction, higherIsBetter);
  return (
    <span className={cn('inline-flex items-center gap-0.5 text-xs', colorClass, className)}>
      <span>{ARROW[direction]}</span>
      <span>{text}</span>
    </span>
  );
}

function resolveColor(direction: DeltaDirection, higherIsBetter: boolean): string {
  if (direction === 'flat') return 'text-muted-foreground';
  const isPositive = (direction === 'up') === higherIsBetter;
  return isPositive ? 'text-success' : 'text-error';
}

/** Utility: format a signed percentage into "+12%" or "-8%" for use as DeltaLabel text. */
export function formatPctText(pct: number | null): string {
  if (pct === null) return '—';
  const rounded = Math.round(pct);
  const sign = rounded > 0 ? '+' : '';
  return `${sign}${rounded}%`;
}

/** Utility: format an absolute WoW difference like "+3" or "-2". */
export function formatWowText(deltaValue: number | null): string {
  if (deltaValue === null) return '—';
  const sign = deltaValue > 0 ? '+' : '';
  return `${sign}${deltaValue}`;
}
